import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

gsap.registerPlugin(ScrollTrigger);
import { insights } from '../data/insights';

const { FiClock, FiArrowRight, FiMail, FiCheckCircle } = FiIcons;

const Insights = () => {
  const [subscribed, setSubscribed] = useState(false);
  const headerRef     = useRef(null);
  const articlesRef   = useRef([]);
  const newsletterRef = useRef(null);
  const push = (b) => (el) => { if (el) b.current.push(el); };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current,
        { autoAlpha: 0, y: 28 },
        { autoAlpha: 1, y: 0, duration: 0.9, ease: 'expo.out' }
      );
      const cards = articlesRef.current.filter(Boolean);
      gsap.fromTo(cards,
        { autoAlpha: 0, y: 36, scale: 0.97 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.65, ease: 'power3.out',
          stagger: { amount: 0.45, from: 'start' },
          scrollTrigger: { trigger: cards[0], start: 'top 86%', once: true } }
      );
      gsap.fromTo(newsletterRef.current,
        { autoAlpha: 0, y: 24 },
        { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: newsletterRef.current, start: 'top 85%', once: true } }
      );
    });
    return () => ctx.revert();
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    setSubscribed(true);
  };

  return (
    <div className="pt-32 pb-0 relative bg-white overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.15]"></div>
      
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 relative z-10 mb-20">
        <div ref={headerRef} style={{ opacity: 0 }} className="max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            <span className="text-gradient-cyan">Insights</span>
          </h1>
          <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
            Practical thinking on software, AI, and building better business systems.
            We share our experience from the front lines of digital transformation.
          </p>
        </div>
      </section>

      {/* Article Grid */}
      <section className="max-w-7xl mx-auto px-6 relative z-10 mb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {insights.map((article, index) => (
            <article
              key={index}
              ref={push(articlesRef)}
              style={{ opacity: 0 }}
              className="bg-white border border-slate-200 flex flex-col hover:border-cyan-300 transition-all duration-500 group shadow-sm hover:shadow-md"
            >
              <div className="p-8 flex flex-col h-full">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600 bg-cyan-50 px-3 py-1 border border-cyan-200">
                    {article.tag}
                  </span>
                  <div className="flex items-center gap-1.5 text-slate-500 text-[10px] font-bold uppercase tracking-widest">
                    <SafeIcon icon={FiClock} />
                    {article.readTime}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-4 group-hover:text-cyan-600 transition-colors leading-tight">
                  {article.title}
                </h3>
                
                <p className="text-slate-600 text-sm leading-relaxed mb-8 flex-grow">
                  {article.excerpt}
                </p>
                
                <Link 
                  to={`/insights/${article.slug}`}
                  className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-slate-900 hover:text-cyan-600 transition-colors group/btn w-fit"
                >
                  Read More
                  <SafeIcon icon={FiArrowRight} className="group-hover/btn:translate-x-1 transition-transform" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Newsletter Strip */}
      <section className="bg-slate-50 border-y border-slate-100 py-20 relative z-10">
        <div ref={newsletterRef} style={{ opacity: 0 }} className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block p-3 bg-cyan-50 rounded-full mb-6">
            <SafeIcon icon={FiMail} className="text-2xl text-cyan-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Get new insights delivered to your inbox</h2>
          <p className="text-slate-600 mb-10">Join our network of business leaders receiving practical tech advice.</p>
          
          {subscribed ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center justify-center gap-3 text-cyan-600 font-bold"
            >
              <SafeIcon icon={FiCheckCircle} className="text-xl" />
              <span>Thank you for subscribing!</span>
            </motion.div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input 
                required
                type="email" 
                placeholder="Enter your email"
                className="flex-grow bg-white border border-slate-200 px-6 py-4 text-slate-900 outline-none focus:border-cyan-500 transition-all text-sm"
              />
              <button 
                type="submit"
                className="bg-slate-900 text-white px-8 py-4 font-black uppercase tracking-widest text-xs hover:bg-black transition-all font-sans shadow-lg shadow-slate-200"
              >
                Subscribe
              </button>
            </form>
          )}
        </div>
      </section>

      <div className="h-24"></div>
    </div>
  );
};

export default Insights;